import React from 'react';
import { motion } from 'framer-motion';
import { Icon } from '@iconify/react';
import { useI18n } from '../../lib/i18n';

// MANDATORY COLORS
const COLORS = {
  primary: '#0052CC',
  secondary: '#0747A6',
  accent: '#00BCC5',
  background: '#F4F5F7',
  text: '#172B4D',
};

const stepImages = [
  '/images/step-1-booking.png',
  '/images/step-2-handover.png',
  '/images/step-3-travel.png',
  '/images/step-4-return.png',
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.1, 0.25, 1.0], // power2.out equivalent
    },
  },
};

const lineVariants = {
  hidden: { width: '0%' },
  visible: {
    width: '100%',
    transition: {
      duration: 1.5,
      ease: 'easeInOut',
    },
  },
};

const verticalLineVariants = {
  hidden: { height: '0%' },
  visible: {
    height: '100%',
    transition: {
      duration: 1.5,
      ease: 'easeInOut',
    },
  },
};

export default function HowItWorksSection1({ className = '' }: { className?: string }) {
  const { t } = useI18n()

  const steps = [
    { id: 1, number: '01', category: t.howItWorks.step1.category, title: t.howItWorks.step1.title, description: t.howItWorks.step1.description, image: stepImages[0] },
    { id: 2, number: '02', category: t.howItWorks.step2.category, title: t.howItWorks.step2.title, description: t.howItWorks.step2.description, image: stepImages[1] },
    { id: 3, number: '03', category: t.howItWorks.step3.category, title: t.howItWorks.step3.title, description: t.howItWorks.step3.description, image: stepImages[2] },
    { id: 4, number: '04', category: t.howItWorks.step4.category, title: t.howItWorks.step4.title, description: t.howItWorks.step4.description, image: stepImages[3] },
  ]

  return (
    <section
      className={`relative py-24 md:py-32 overflow-hidden ${className}`}
      style={{ backgroundColor: COLORS.background }}
    >
      {/* Subtle Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/50 to-transparent pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold mb-6"
            style={{ color: COLORS.text }}
          >
            {t.howItWorks.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-lg md:text-xl"
            style={{ color: '#64748B' }}
          >
            {t.howItWorks.subtitle}
          </motion.p>
        </div>

        {/* Process Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="relative grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-8"
        >
          {/* Desktop Connector Line */}
          <div className="hidden md:block absolute top-[280px] left-0 right-0 h-[1px] bg-slate-200 -z-10">
            <motion.div
              variants={lineVariants}
              className="h-full origin-left"
              style={{ backgroundColor: COLORS.primary }}
            />
          </div>

          {/* Mobile Connector Line */}
          <div className="md:hidden absolute top-0 bottom-0 left-[2rem] w-[1px] bg-slate-200 -z-10">
             <motion.div
              variants={verticalLineVariants}
              className="w-full origin-top"
              style={{ backgroundColor: COLORS.primary }}
            />
          </div>

          {steps.map((step, index) => (
            <motion.div
              key={step.id}
              variants={cardVariants}
              className="group relative flex flex-col md:block pl-16 md:pl-0"
            >
              {/* Mobile Number Indicator (Absolute Left) */}
              <div className="md:hidden absolute left-0 top-0 w-16 flex justify-center">
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-md z-10 border-4 border-[#F4F5F7]"
                  style={{ backgroundColor: COLORS.primary }}
                >
                  {step.id}
                </div>
              </div>

              {/* Card */}
              <div 
                className="bg-white rounded-2xl overflow-hidden shadow-sm border transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-lg flex flex-col h-full"
                style={{ borderColor: 'rgba(0,0,0,0.04)' }}
              >
                {/* Image Container */}
                <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                  <motion.img
                    src={step.image}
                    alt={step.title}
                    
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  {/* Overlay gradient for text readability if needed, though design is clean */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col flex-grow">
                  {/* Step Number & Category */}
                  <div className="flex items-center justify-between mb-3">
                    <span 
                      className="text-xs font-mono tracking-widest uppercase font-bold transition-colors duration-300 group-hover:text-[#00BCC5]"
                      style={{ color: COLORS.primary }}
                    >
                      STEP {step.number}
                    </span>
                    <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                      {step.category}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 
                    className="text-xl font-semibold mb-3"
                    style={{ color: COLORS.text }}
                  >
                    {step.title}
                  </h3>

                  {/* Description */}
                  <p className="text-sm leading-relaxed text-slate-600">
                    {step.description}
                  </p>
                </div>
              </div>

              {/* Desktop Number Indicator (Below Card, on Line) */}
              <div className="hidden md:flex justify-center mt-8 relative z-10">
                <div 
                  className="w-10 h-10 rounded-full bg-white border flex items-center justify-center text-sm font-bold transition-colors duration-300 group-hover:border-[#00BCC5] group-hover:text-[#00BCC5]"
                  style={{ 
                    borderColor: '#E2E8F0', 
                    color: COLORS.text 
                  }}
                >
                  {step.number}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}